//外部依赖
import { useState } from 'react';
//内部组件和方法
import CDNImage from '@/components/cdnImage/cdnImage';
import JDIPrice from '@/components/price';
import {
  comparePrice,
  formatStyle,
  getExptag,
  imgTipsStyle,
} from '@/components/productCard/common/utils';
import SafeLink from '@/components/SafeLink';
import UKProductSelDrawer from '@/components/UKProductSelDrawer';
import { formatProductLink } from '@/utils/formatProductLink';
import { gotoDp } from '@/utils/goto';
import Rate from './rate';
//本地图片
import AddCartIcon from '@/assets/icons/addBlack.svg';
//多语言
import { useTranslation } from '@/i18n/client';
//样式
import style from './style.module.scss';
//接口和ts
import type { productData_4 } from '@/components/productCard/index.interface';
import { addCartUtil } from '@/utils/cartUtils';
import { useStores } from '@/models';

interface PropsType {
  productData: productData_4;
}

export default function ProductCard_1({ productData }: PropsType) {
  let {
    width,
    height,
    containerStyle,
    shouldShowSkuName = true,
    skuName,
    skuNameStyle,
    skuImgKeyParams,
    skuImg,
    score,
    scoreStyle,
    saleNum,
    saleNumStyle,
    skuImgStyle,
    skuId,
    shouldShowPrice = true,
    priceStyle,
    realPrice,
    realPriceJDIPriceParams,
    originalPrice,
    originalPriceJDIPriceParams,
    currency,
    languageCode,
    imgTips,
    shouldShowCart,
    onClickCart,
    onAddCartSuccess,
    shouldShowModal,
    shouldShowService = false,
    jumpTarget,
    jumpUrl,
    onClickProduct,
    shouldShowMarketing = true,
    marketingStyle,
    marketingArray,
    isOutOfStock,
    isRemoved,
    trackingPointParams,
    shipmentType,
  } = productData;
  // @ts-ignore
  containerStyle = Object.assign({}, containerStyle, {
    width: formatStyle(width),
    height: formatStyle(height),
  });

  const { t } = useTranslation();
  const { cartNumStore } = useStores();
  const [isShowProductModal, setIsShowProductModal] = useState(false); // 是否显示选择商品类型弹窗

  //点击商卡事件
  const handleProductClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();
    if (trackingPointParams && trackingPointParams.productClickPoint) {
      trackingPointParams.productClickPoint();
    }
    // 检查 onClickCart 是否存在并且是一个函数
    if (onClickProduct && typeof onClickProduct === 'function') {
      onClickProduct(productData);
    } else {
      jumpUrl ? (window.location.href = jumpUrl) : gotoDp(skuId);
    }
  };

  //点击购物车事件
  const handleCartClick = (e: any, productData: productData_4) => {
    e.preventDefault();
    e.stopPropagation();
    if (trackingPointParams && trackingPointParams.cartClickPoint) {
      trackingPointParams.cartClickPoint();
    }
    // 检查 onClickCart 是否存在并且是一个函数
    if (onClickCart && typeof onClickCart === 'function') {
      onClickCart(productData);
    } else {
      //加车时是否展示规格弹层
      if (shouldShowModal) {
        setIsShowProductModal(true);
      } else {
        //直接加车
        loadAddCart({ skuId });
      }
    }
  };

  //调用加车方法
  const loadAddCart = ({ skuId }: any) => {
    addCartUtil([{ skuId, num: '1' }], false, shipmentType).then((res) => {
      if (trackingPointParams && trackingPointParams.addCartPoint) {
        trackingPointParams.addCartPoint();
      }
      if (res?.result?.code === '200') {
        // 更新购物车数量
        cartNumStore.updateCartNum(res?.result?.data);
      }
      //加车接口调用成功回调方法
      if (onAddCartSuccess && typeof onAddCartSuccess === 'function') onAddCartSuccess(productData);
    });
  };

  const handleClose = () => {
    setIsShowProductModal(false);
  };

  return (
    <div
      className={style.UK_product_card}
      style={containerStyle}
      key={skuId}
      exptag={getExptag('exptag', trackingPointParams, 'productExpConfig')}
      data-exptag-config={getExptag('config', trackingPointParams, 'productExpConfig')}
      data-exptag-json-param={getExptag('json-param', trackingPointParams, 'productExpConfig')}
      data-exptag-event-param={getExptag('event-param', trackingPointParams, 'productExpConfig')}
    >
      <SafeLink
        href={jumpUrl ? jumpUrl : formatProductLink(skuId, skuName)}
        target={jumpTarget}
        onClick={(e) => handleProductClick(e)}
      >
        {/* 商品图片 */}
        <div className={style.UK_image_wrapper}>
          <CDNImage
            className={`${style.UK_skuImg} ${isOutOfStock || isRemoved ? style.op50 : ''}`}
            src={(skuImgKeyParams ? skuImgKeyParams?.imgUrlKey : skuImg) as string}
            width={skuImgKeyParams?.width || 480}
            height={skuImgKeyParams?.height || 480}
            style={skuImgStyle}
            alt={skuName}
          />

          <div className={style.UK_mask}></div>
          {/* 是否展示无货状态 */}
          {isRemoved ? (
            <div className={style.UK_outOfStock}>
              <div className={style.UK_outOfStock_text}>{t('common.productStatus.removed')}</div>
            </div>
          ) : isOutOfStock ? (
            <div className={style.UK_outOfStock}>
              <div className={style.UK_outOfStock_text}>{t('common.productStatus.outOfStock')}</div>
            </div>
          ) : null}
          {imgTips && imgTips.tipDom ? (
            <span className={style.UK_imgTips} style={imgTipsStyle(imgTips.position)}>
              {imgTips.tipDom}
            </span>
          ) : null}
        </div>
        {/* 商品名称 */}
        {shouldShowCart && !isRemoved ? (
          <div
            className={style.UK_right}
            exptag={getExptag('exptag', trackingPointParams, 'cartExpConfig')}
            data-exptag-config={getExptag('config', trackingPointParams, 'cartExpConfig')}
            data-exptag-json-param={getExptag('json-param', trackingPointParams, 'cartExpConfig')}
            data-exptag-event-param={getExptag('event-param', trackingPointParams, 'cartExpConfig')}
          >
            <div
              style={{
                marginTop: '12px',
                display: 'flex',
              }}
            >
              <p
                onClick={(e: any) => handleCartClick(e, productData)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '9px 24px',
                  gap: '4px',
                  borderRadius: '2px',
                  background: 'var(--Text-Primary, #1a1a1a)',
                  color: 'var(--Text-Anti-Black, #FFF)',
                  fontSize: '14px',
                  fontStyle: 'normal',
                  fontWeight: '600',
                  lineHeight: '20px' /* 142.857% */,
                }}
              >
                <AddCartIcon />
                {t('cart.addToCart')}
              </p>{' '}
            </div>
            {/* <AddCartIcon className={style.UK_cart}></AddCartIcon> */}
          </div>
        ) : null}
        {shouldShowSkuName && (
          <div className={style.UK_name_wrapper} style={skuNameStyle}>
            {/* 折扣标签 */}
            {/* {saleTipsDom ? <div className={style.UK_saleTipsDom}>{saleTipsDom}</div> : null} */}
            <span className={style.UK_skuName}>{skuName}</span>
          </div>
        )}
      </SafeLink>

      {/* 评分、库存、采销推荐 */}
      {score || saleNum ? (
        <div className={style.UK_score} style={scoreStyle}>
          {score ? (
            <>
              <Rate scoreNum={Number(score)} />
              <p className={style.UK_star}>{score}</p>
            </>
          ) : null}
          {saleNum ? (
            <span className={style.UK_saleNum} style={saleNumStyle}>
              {saleNum}
            </span>
          ) : null}
        </div>
      ) : (
        <div className={style.UK_score}></div>
      )}
      {/* 商品价格*/}
      {shouldShowPrice && (
        <div className={style.UK_price_and_cart_wrapper}>
          <div className={style.UK_left}>
            <div className={style.UK_price} style={priceStyle}>
              <JDIPrice
                {...realPriceJDIPriceParams}
                locale={languageCode}
                currency={currency || ''}
                price={realPrice}
                uiType={1}
                containerStyle={{
                  ...realPriceJDIPriceParams?.containerStyle,
                  marginRight: '8px',
                }}
              />
              {originalPrice && comparePrice(originalPrice, realPrice) && (
                <JDIPrice
                  {...originalPriceJDIPriceParams}
                  locale={languageCode}
                  currency={currency || ''}
                  price={originalPrice}
                  uiType={2}
                  lineThrough
                />
              )}
            </div>
          </div>
        </div>
      )}
      {/* 底部营销标签 */}
      {shouldShowMarketing && marketingArray && marketingArray.length > 0
        ? marketingArray.map((item, i) => {
            return (
              <div className={style.UK_activityRow} key={i} style={marketingStyle}>
                {item}
              </div>
            );
          })
        : null}
      <UKProductSelDrawer
        skuId={skuId}
        triggerOpen={isShowProductModal}
        handleClose={handleClose}
        updateItemData={loadAddCart}
        isShowService={shouldShowService}
      />
    </div>
  );
}
